import SimulatorConfiguration from './SimulatorHelper';
import { appVersion } from '../context/AppVersionContext';
import * as ClubFitting from '../classes/ClubFitting';
import Golfer, { EnhancedFittingData } from '../classes/Golfer';
import Fitter from '../classes/Fitter';

//Enhanced fitting data
export const getClubFittingPayload = (golfer: Golfer, fitter: Fitter, simulatorConfiguration: SimulatorConfiguration) => {

    let clubFittingPayload = new ClubFitting.ClubFittingDataPayload();

    //Header
    let header = new ClubFitting.Header((window.crypto as any)['randomUUID'](), golfer.timestamp.toISOString(), new Date().toISOString(), appVersion);
    clubFittingPayload.header = header;

    //AppContext - app based questions
    let appContext = new ClubFitting.AppContext();
    appContext.settings.push(new ClubFitting.Question("Model", simulatorConfiguration.modelType));
    appContext.settings.push(new ClubFitting.Question("Connection Type", simulatorConfiguration.connectionType));
    appContext.settings.push(new ClubFitting.Question("UOM", fitter.speedUnits));
    appContext.settings.push(new ClubFitting.Question("Distance Units", fitter.distanceUnits));
    appContext.settings.push(new ClubFitting.Question("Language", fitter.language));
    appContext.settings.push(new ClubFitting.Question("Fitting Type", fitter.fittingType));
    appContext.settings.push(new ClubFitting.Question("Shots Per Club", fitter.shotsPerClub.toString()));
    appContext.settings.push(new ClubFitting.Question("Use Estimate", fitter.useEstimateDynamicLoft ? "True" : "False"));
    appContext.settings.push(new ClubFitting.Question("Hide Email", fitter.hideGolferEmail ? "True" : "False"));
    clubFittingPayload.appContext = appContext;

    //FittingContext - golfer specific questions and club preferences
    let fitterPerson = new ClubFitting.Person(fitter.firstName, fitter.lastName, fitter.email, "fitter");
    let golferPerson = new ClubFitting.Person(golfer.firstName, golfer.lastName, golfer.email, "golfer", "", golfer.handicap, "");

    let survey = new Array<ClubFitting.Question>();
    survey.push(new ClubFitting.Question("Dominant Hand", golfer.hand));
    survey.push(new ClubFitting.Question("Handicap", golfer.handicap));
    survey.push(new ClubFitting.Question("Initial Course Conditions", golfer.origCourseConditions));
    survey.push(new ClubFitting.Question("Initial Bunker Conditions", golfer.origBunkerConditions));
    survey.push(new ClubFitting.Question("Initial Bunker Confidence", golfer.origConfidence));
    survey.push(new ClubFitting.Question("Course Conditions", golfer.courseConditions));
    survey.push(new ClubFitting.Question("Bunker Conditions", golfer.bunkerConditions));
    survey.push(new ClubFitting.Question("Bunker Confidence", golfer.confidence));

    let clubShotPreferences = new Array<ClubFitting.ClubShotPreference>();
    golfer.selectedLofts.forEach(loft => {
        let club = new ClubFitting.Club(loft.lineNumber, new ClubFitting.Head("wedge", loft.loft));
        let shotTypes = new Array<ClubFitting.ShotType>();
        let shots = loft.shots.sort((a, b) => a.priority - b.priority);
        loft.shots.forEach(shot => {
            if (shot.selected) {
                shotTypes.push(new ClubFitting.ShotType("ground", shot.shotType.toLowerCase(), (shot.priority + 1).toString()))
            }
        })
        clubShotPreferences.push(new ClubFitting.ClubShotPreference(club, shotTypes));
    })

    let fittingContext = new ClubFitting.FittingContext(fitterPerson, golferPerson, survey, clubShotPreferences);
    clubFittingPayload.fittingContext = fittingContext;

    // ClubConfigurationTests - practice shots and the resulting grind recommendations
    // get distinct list of lofts from SelectedLofts collection. Then get all practice shots for each loft from the LoftsForFittingCollection
    golfer.selectedLofts.forEach((loft, lineNumber) => {
        let clubConfigurationTest = new ClubFitting.ClubConfigurationTest();
        clubConfigurationTest.club = new ClubFitting.Club(loft.lineNumber, new ClubFitting.Head("wedge", loft.loft), new ClubFitting.Shaft(), new ClubFitting.Grip());
        clubConfigurationTest.person = new ClubFitting.Person(golfer.firstName, golfer.lastName, golfer.email, "golfer", "", golfer.handicap, "");

        //LoftsForFitting can have the same loft duplicated with different shot types. Need to flatten the practice shot data by loft only
        // golfer.loftsForFitting.filter(loftCurrent => loftCurrent.loft === loft.loft).forEach(loftForFitting => {
            //for each loft find all the related shots in the enhancedfittingdata collection
            var fittings = golfer.enhancedFittingData.filter(f => f.loft === loft.loft).forEach(fitting => {
                let shot = new ClubFitting.Shot();
                shot.lineNumber = loft.lineNumber;
                shot.startTime = fitting.datetime;
                shot.endTime = fitting.datetime;
                shot.device = new ClubFitting.Device(simulatorConfiguration.modelType, "", "");
                shot.club = clubConfigurationTest.club;
                shot.isDeleted = fitting.isDeleted;
                shot.shotType = new ClubFitting.ShotType("ground", fitting.shotType);
                shot.devicePayload = new ClubFitting.DevicePayload(fitting.id, "measurement", fitting.message);
                shot.shotAnalysis = new ClubFitting.ShotAnalysis(fitting.attackAngle, fitting.shaftLean, fitting.ballSpeed, fitting.launchAngle);
                //copy over the grind recommendations
                fitting.recommendations.forEach(recommendation => {
                    shot.shotRecommendations.push(new ClubFitting.ClubRecommendation(new ClubFitting.Club(recommendation.lineNumber, new ClubFitting.Head("wedge", recommendation.loft, recommendation.selectedBounce, recommendation.selectedGrind), new ClubFitting.Shaft(), new ClubFitting.Grip()), "false", "", []));
                })
                clubConfigurationTest.shots.push(shot);
            // })
        })
        //save to the payload collection
        clubFittingPayload.clubConfigurationTests.push(clubConfigurationTest);

    })

    //fitting recommendations 
    golfer.grindRecommendations.forEach(grind => {
        grind.grindWeightings.forEach(weighting => {
            let reccomendation = new ClubFitting.ClubRecommendation();
            reccomendation.club = new ClubFitting.Club(grind.lineNumber, new ClubFitting.Head("wedge", grind.loft, weighting.bounce, weighting.grind), new ClubFitting.Shaft(), new ClubFitting.Grip());
            let selected = weighting.bounce == grind.selectedBounce && weighting.grind === grind.selectedGrind;
            reccomendation.selected = selected.toString();
            reccomendation.rankPercent = weighting.weightPercent.toString();
            if (selected) {
                grind.notes.forEach(note => {
                    if (note.name !== "") {
                        reccomendation.reasons.push(note);
                    }
                })
            }
            //save to the payload collection
            clubFittingPayload.fittingRecommendations.push(reccomendation);
        });
    })
    return clubFittingPayload;
}

export const setIsDeleted = (id: string, enhancedFittingData: EnhancedFittingData[]) => {
    var fitting = enhancedFittingData.find(f => f.id === id);
    if (fitting){
        fitting.isDeleted = "true";
    }
    return enhancedFittingData;
}

